import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, updateDoc, doc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { motion } from 'framer-motion';
import { FileText, Download, Search, Filter, Calendar, ExternalLink, Info, Loader2, Grid } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

interface Regulation {
  id: string;
  title: string;
  description: string;
  year: number;
  category: string;
  fileUrl: string;
  downloadCount: number;
  createdAt: any;
}

export default function Regulations() {
  const [regulations, setRegulations] = useState<Regulation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Semua');

  useEffect(() => {
    fetchRegulations();
  }, []);

  const fetchRegulations = async () => {
    try {
      const q = query(collection(db, 'regulations'), orderBy('year', 'desc'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Regulation[];
      setRegulations(data);
    } catch (error) {
      console.error('Error fetching regulations:', error);
      toast.error('Gagal memuat data regulasi');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['Semua', ...Array.from(new Set(regulations.map(r => r.category)))];

  const filteredRegulations = regulations.filter(reg => {
    const matchesSearch = reg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         reg.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Semua' || reg.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Define category priority (Hierarchy)
  const categoryPriority: Record<string, number> = {
    'Undang-Undang': 1,
    'Peraturan Pemerintah': 2,
    'Peraturan President': 3,
    'Peraturan Menteri': 4,
    'Keputusan Menteri': 5,
    'Peraturan Lembaga': 6,
    'Keputusan Lembaga': 7,
    'Surat Edaran': 8
  };

  // Group and sort regulations
  const groupedRegulations = filteredRegulations.reduce((acc, reg) => {
    if (!acc[reg.category]) {
      acc[reg.category] = [];
    }
    acc[reg.category].push(reg);
    return acc;
  }, {} as Record<string, Regulation[]>);

  // Sort categories by priority, then alphabetically for unknown categories
  const sortedCategoryNames = Object.keys(groupedRegulations).sort((a, b) => {
    const priorityA = categoryPriority[a] || 99;
    const priorityB = categoryPriority[b] || 99;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return a.localeCompare(b);
  });

  // Ensure items within each group are sorted by year (already handled by fetch order, but double checking here)
  Object.values(groupedRegulations).forEach(regs => {
    regs.sort((a, b) => b.year - a.year);
  });

  const handleDownload = async (reg: Regulation) => {
    try {
      // Increment download count in Firestore
      const regRef = doc(db, 'regulations', reg.id);
      await updateDoc(regRef, {
        downloadCount: increment(1)
      });
      
      // Update local state
      setRegulations(prev => prev.map(r => 
        r.id === reg.id ? { ...r, downloadCount: r.downloadCount + 1 } : r
      ));

      // Trigger download
      window.open(reg.fileUrl, '_blank');
      toast.success('Download dimulai...');
    } catch (error) {
      console.error('Download error:', error);
      // Even if update fails, we try to open the link
      window.open(reg.fileUrl, '_blank');
    }
  };

  const scrollToCategory = (catId: string) => {
    const element = document.getElementById(catId);
    if (element) {
      const offset = 100;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="pt-24 min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative py-20 overflow-hidden bg-navy text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_120%,rgba(201,161,74,0.3),transparent)]"></div>
          <Grid className="w-full h-full text-gold/20" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-4 bg-gold/20 text-gold-dark dark:text-gold border-gold/30 px-4 py-1 font-bold">Pusat Data Regulasi</Badge>
            <h1 className="text-4xl md:text-6xl font-black mb-6 tracking-tighter">
              Regulasi & <span className="text-gold">Peraturan</span> Konstruksi
            </h1>
            <p className="text-white/60 max-w-2xl mx-auto text-lg">
              Akses cepat ke berbagai dokumen peraturan, undang-undang, dan pedoman teknis terbaru dalam industri konstruksi di Indonesia.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Cari judul regulasi atau deskripsi..."
              className="pl-12 h-12 bg-card border-border/50 focus:border-gold/50"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <select
              className="w-full h-12 pl-12 pr-4 bg-card border border-border/50 rounded-md focus:border-gold/50 outline-none appearance-none font-medium"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <Button 
            className="h-12 bg-muted text-foreground hover:bg-muted/80 font-bold"
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('Semua');
            }}
          >
            Reset Filter
          </Button>
        </div>

        {/* Quick Navigation Chips */}
        {selectedCategory === 'Semua' && sortedCategoryNames.length > 1 && !searchTerm && (
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-1 w-4 bg-gold rounded-full"></div>
              <span className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Lompat ke Kategori</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {sortedCategoryNames.map(catName => (
                <button
                  key={catName}
                  onClick={() => scrollToCategory(`cat-${catName.replace(/\s+/g, '-').toLowerCase()}`)}
                  className="px-4 py-2.5 rounded-xl bg-gold/5 border border-gold/20 text-xs font-bold hover:bg-gold/10 hover:border-gold/40 transition-all active:scale-95 shadow-sm"
                >
                  {catName}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Loader2 className="h-12 w-12 text-gold animate-spin" />
            <p className="text-muted-foreground font-medium">Memuat data regulasi...</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <p className="text-muted-foreground font-medium">
                Menampilkan <span className="text-foreground font-bold">{filteredRegulations.length}</span> dokumen
              </p>
            </div>

            <div className="space-y-16">
              {sortedCategoryNames.map((catName) => (
                <div key={catName} id={`cat-${catName.replace(/\s+/g, '-').toLowerCase()}`} className="scroll-mt-32">
                  <div className="flex items-center gap-4 mb-8">
                    <h2 className="text-2xl font-black tracking-tight">{catName}</h2>
                    <div className="h-[2px] flex-grow bg-gradient-to-r from-gold/20 to-transparent"></div>
                    <Badge className="bg-gold/10 text-gold border-gold/20">{groupedRegulations[catName].length} Dokumen</Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {groupedRegulations[catName].map((reg, index) => (
                      <motion.div
                        key={reg.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05, duration: 0.5 }}
                      >
                        <Card className="group relative h-full flex flex-col bg-card/40 backdrop-blur-md border border-border/50 hover:border-gold/30 hover:shadow-[0_20px_50px_rgba(201,161,74,0.1)] transition-all duration-500 rounded-[2rem] overflow-hidden">
                          {/* Top Accent Line */}
                          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gold/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          
                          <CardContent className="p-8 flex flex-col h-full relative">
                            <div className="flex items-start justify-between mb-6">
                              <div className="relative">
                                <div className="absolute inset-0 bg-gold/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <div className="relative p-4 rounded-2xl bg-gradient-to-br from-gold/20 to-gold/5 text-gold border border-gold/20 shadow-inner group-hover:scale-110 transition-transform duration-500">
                                  <FileText className="h-6 w-6" />
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-foreground/50">Reference</span>
                                <span className="text-[11px] font-bold text-foreground/30 group-hover:text-gold/60 transition-colors uppercase tracking-tight">
                                  ID-{reg.id.slice(0, 8).toUpperCase()}
                                </span>
                              </div>
                            </div>

                            <div className="space-y-4 flex-grow">
                              <h3 className="text-xl font-black leading-tight text-foreground group-hover:text-gold transition-colors duration-300">
                                {reg.title}
                              </h3>
                              
                              <p className="text-foreground/70 text-sm leading-relaxed line-clamp-3">
                                {reg.description}
                              </p>
                            </div>

                              <div className="mt-8 pt-6 border-t border-border/30 flex flex-col sm:flex-row items-center justify-between gap-6">
                                <div className="flex items-center gap-6">
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-[9px] font-black uppercase tracking-widest text-foreground/50">Tahun</span>
                                    <div className="flex items-center gap-1.5 text-sm font-bold text-foreground/90">
                                      <Calendar className="h-3.5 w-3.5 text-gold" />
                                      <span>{reg.year}</span>
                                    </div>
                                  </div>
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-[9px] font-black uppercase tracking-widest text-foreground/50">Akses</span>
                                    <div className="flex items-center gap-1.5 text-sm font-bold text-foreground/90">
                                      <Download className="h-3.5 w-3.5 text-gold" />
                                      <span>{reg.downloadCount || 0}</span>
                                    </div>
                                  </div>
                                </div>
                              
                              <Button 
                                onClick={() => handleDownload(reg)}
                                className="w-full sm:w-auto bg-gold hover:bg-gold-light text-navy font-black rounded-2xl px-6 py-6 shadow-xl shadow-gold/20 transition-all active:scale-95 flex items-center justify-center gap-3 group/btn"
                              >
                                <span className="uppercase text-xs tracking-widest">Unduh File</span>
                                <div className="bg-navy/10 p-1 rounded-lg">
                                  <Download className="h-4 w-4 group-hover/btn:translate-y-0.5 transition-transform" />
                                </div>
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {filteredRegulations.length === 0 && (
              <div className="text-center py-24 bg-muted/20 rounded-3xl border border-dashed border-border">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4 text-muted-foreground">
                  <Info className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-2">Tidak ditemukan</h3>
                <p className="text-muted-foreground">Coba ubah kata kunci atau filter Anda.</p>
              </div>
            )}
          </>
        )}

        {/* Disclaimer / Notice */}
        <div className="mt-20 p-8 rounded-3xl bg-muted/40 border border-border/50 text-center">
          <h4 className="text-sm font-black uppercase tracking-widest text-foreground/60 mb-4">Pemberitahuan Penting</h4>
          <p className="text-foreground/80 text-sm max-w-3xl mx-auto leading-relaxed italic">
            Semua dokumen yang disediakan di halaman ini bersifat publik. Kami tidak menyimpan salinan dokumen yang dibatasi secara hukum. Jika Anda memerlukan dokumen teknis spesifik yang tidak tercantum, silakan hubungi tim layanan kami melalui menu Pusat Bantuan.
          </p>
        </div>
      </div>
    </div>
  );
}
